import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Checkbox, Message, SelectPicker } from 'rsuite';
import PageHeader from '../components/shared/PageHeader';
import Section from '../components/shared/Section';
import { TutorialButton } from '@nsi-advisory-services/components';
import { useAuth } from '../contexts/AuthContext';
import { useTimesheetTutorial, TIMESHEET_TUTORIAL } from '../tutorials/timesheetTutorial';
import { showToast } from '../utils/toast';
import {
  useDeleteTimesheetEntry,
  usePeriodAction,
  useSaveTimesheetEntry,
  useTimesheetConfig,
  useTimesheetEmployees,
  useTimesheetEntries,
  useTimesheetPeriod,
  useTimesheetTasks,
  useTimesheetTimer,
  useTimesheetValidation,
} from '../hooks/useTimesheet';
import {
  Banner,
  ProviderBanner,
  DateStepper,
  TimesheetNav,
  StatusPill,
  ValidationBanners,
  addToClock,
  suggestStartTime,
} from '../components/timesheet/TimesheetParts';
import { findOpenSlot, findOverlap } from '../components/timesheet/overlapFix';
import PeriodGrid from '../components/timesheet/PeriodGrid';
import WeekColumns from '../components/timesheet/WeekColumns';
import PeriodActions from '../components/timesheet/PeriodActions';
import PeriodCard from '../components/timesheet/PeriodCard';
import DayView from '../components/timesheet/DayView';
import CalendarEvents from '../components/timesheet/CalendarEvents';
import EntryModal from '../components/timesheet/EntryModal';
import LogTimeBar from '../components/timesheet/LogTimeBar';
import PeriodActionModal from '../components/timesheet/PeriodActionModal';
import SlideSwap from '../components/timesheet/SlideSwap';
import {
  addDays,
  formatDayHeading,
  formatHM,
  formatPeriod,
  formatRange,
  formatTimestamp,
  groupValidationIssues,
  breakClock,
  formatClock12,
  formatDate,
  isValidIso,
  isWeekend,
  listDays,
  nextPeriod,
  periodFor,
  previousPeriod,
  todayIso,
  weekFor,
} from '../utils/timesheet';
import './Timesheet.css';
import './TimesheetLayout.css';

const VIEWS = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'period', label: 'Pay period' },
];

const PERIOD_ACTIONS = {
  submit: {
    size: 'sm',
    title: 'Submit for Approval',
    confirmLabel: 'Submit',
    commentLabel: 'Note for your reviewer',
    done: 'Timesheet submitted for approval.',
  },
  approve: {
    title: 'Approve Timesheet',
    confirmLabel: 'Approve',
    commentLabel: 'Comment',
    done: 'Timesheet approved.',
  },
  return: {
    title: 'Return Timesheet',
    confirmLabel: 'Return',
    confirmColor: 'orange',
    commentLabel: 'What needs to change?',
    commentRequired: true,
    done: 'Timesheet returned.',
  },
  reopen: {
    title: 'Reopen Timesheet',
    confirmLabel: 'Reopen',
    commentLabel: 'Reason',
    done: 'Timesheet reopened.',
  },
};

// Kept in this browser, as the theme is.
const HIDE_WEEKENDS_KEY = 'ems.timesheet.hideWeekends';

const readHideWeekends = () => {
  try {
    return window.localStorage.getItem(HIDE_WEEKENDS_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeHideWeekends = (hide) => {
  try {
    window.localStorage.setItem(HIDE_WEEKENDS_KEY, String(hide));
  } catch {
    // Not remembered between visits; the checkbox still works.
  }
};

/**
 * My Timesheet.
 *
 * A range bar for how you are looking (day, week or pay period) and where;
 * under it the view on the left - the day as an agenda,
 * the week as a column per day, the pay period as a heat map of task by day.
 * The day view has a sidebar on the right with the timer / log time card and the
 * pay period card. The week and pay period views use the full width, so submitting
 * and reviewing (per pay period, the unit payroll runs on) sit in the range bar there.
 *
 * The page also serves reviewers and managers looking at somebody else's
 * timesheet (?employee=<id>); what they can do there is whatever the server's
 * period `actions` say, so the page never has to second-guess permissions.
 */
export default function TimesheetPage() {
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = todayIso();

  // `date` is the anchor; `start` and `day` are older links' names for it.
  const rawDate = [searchParams.get('date'), searchParams.get('day'), searchParams.get('start')].find(isValidIso);
  const date = rawDate || today;
  const view = VIEWS.some((v) => v.key === searchParams.get('view')) ? searchParams.get('view') : 'day';
  const period = useMemo(() => periodFor(date), [date]);
  const week = useMemo(() => weekFor(date), [date]);
  const visibleDays = useMemo(
    () => (view === 'week' ? listDays(week.start, week.end) : listDays(period.start, period.end)),
    [view, week, period]
  );
  // One fetch covers the day, its week and its pay period, whichever is shown.
  const fetchStart = week.start < period.start ? week.start : period.start;
  const fetchEnd = week.end > period.end ? week.end : period.end;

  const employeeParam = searchParams.get('employee') || '';
  const isSelf = !employeeParam || employeeParam === String(auth.employee?.id || '');
  const employeeId = isSelf ? undefined : employeeParam;

  const updateParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    ['start', 'day'].forEach((legacy) => next.delete(legacy));
    if (!next.get('date') && rawDate) next.set('date', rawDate);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    });
    setSearchParams(next, { replace: true });
  };
  const goTo = (iso) => updateParams({ date: iso === today ? null : iso, highlight: null });

  const step = (dir) => {
    if (view === 'day') goTo(addDays(date, dir));
    else if (view === 'week') goTo(addDays(date, dir * 7));
    else goTo((dir > 0 ? nextPeriod(period) : previousPeriod(period)).start);
  };

  const configQuery = useTimesheetConfig();
  const config = configQuery.data;
  const installed = config?.installed !== false;
  const perms = config?.permissions || {};
  const canPickEmployee = perms.canApprove || perms.canReportAll || perms.canManage;

  const tasksQuery = useTimesheetTasks({ includeArchived: true }, { enabled: installed });
  const entriesQuery = useTimesheetEntries({ employeeId, start: fetchStart, end: fetchEnd }, { enabled: installed });
  const periodQuery = useTimesheetPeriod({ employeeId, start: period.start }, { enabled: installed });
  // The View Hours checks over the pay period on screen - the unit they are
  // written for, so the period-wide rules (billable overtime) mean the same.
  const validationQuery = useTimesheetValidation(
    { employeeId, start: period.start, end: period.end },
    { enabled: installed && Boolean(config) }
  );
  const employeesQuery = useTimesheetEmployees({ enabled: Boolean(canPickEmployee) });
  // Shared cache with the entry bar; only needed here to keep a running timer
  // reachable when the period on screen is locked.
  const timerQuery = useTimesheetTimer({ enabled: installed && isSelf });
  const periodAction = usePeriodAction();
  const saveEntry = useSaveTimesheetEntry();
  const deleteEntry = useDeleteTimesheetEntry();

  const tasks = useMemo(() => tasksQuery.data?.tasks || [], [tasksQuery.data]);
  const taskById = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const entries = useMemo(() => entriesQuery.data?.entries || [], [entriesQuery.data]);
  const periodData = periodQuery.data;
  const issues = useMemo(() => groupValidationIssues(validationQuery.data?.validations), [validationQuery.data]);
  const issueCount = issues.failCount + issues.warnCount;
  const actions = periodData?.actions || {};
  const editable = Boolean(actions.canEdit) && installed;
  const status = periodData?.period?.status || 'open';

  const entriesForDate = useCallback((d) => entries.filter((e) => e.date === d), [entries]);
  const visibleEntries = useMemo(
    () => entries.filter((e) => e.date >= visibleDays[0] && e.date <= visibleDays[visibleDays.length - 1]),
    [entries, visibleDays]
  );

  const cellMap = useMemo(() => {
    const map = new Map();
    visibleEntries.forEach((e) => {
      const key = `${e.taskId}|${e.date}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
    return map;
  }, [visibleEntries]);

  // In the task list's order, which follows the project order set in
  // Timesheet Admin; a task the list doesn't have goes last.
  const rowTaskIds = useMemo(() => {
    const position = new Map(tasks.map((t, idx) => [t.id, idx]));
    const logged = [...new Set(visibleEntries.map((e) => e.taskId))];
    logged.sort((a, b) => (position.get(a) ?? tasks.length) - (position.get(b) ?? tasks.length));
    return logged;
  }, [visibleEntries, tasks]);

  // The pay period's figures, for the submit bar, whatever the view.
  const periodEntries = entries.filter((e) => e.date >= period.start && e.date <= period.end);
  const periodSeconds = periodEntries.reduce((s, e) => s + e.durationSeconds, 0);
  const periodBillableSeconds = periodEntries.reduce((s, e) => s + (e.billable ? e.durationSeconds : 0), 0);
  const missingDays = listDays(period.start, period.end)
    .filter((d) => !isWeekend(d) && d <= today)
    .filter((d) => !periodEntries.some((e) => e.date === d));

  // Entry modal: { entry } to view or edit one.
  const [modal, setModal] = useState(null);
  const [actionKey, setActionKey] = useState(null);
  const [actionError, setActionError] = useState(null);

  const openCell = (taskId, d, cell) => {
    if (cell.length === 1) setModal({ entry: cell[0] });
    else updateParams({ view: null, date: d, highlight: taskId });
  };

  // Same task, note and length, starting where the day's last entry ended. If
  // that would run past midnight it keeps the original's times instead.
  const duplicateEntry = async (entry) => {
    const timed = Boolean(entry.startTime && entry.endTime);
    const start = timed ? suggestStartTime(entriesForDate(entry.date)) : null;
    const end = start ? addToClock(start, entry.durationSeconds) : null;
    try {
      await saveEntry.mutateAsync({
        employeeId,
        taskId: entry.taskId,
        date: entry.date,
        durationSeconds: entry.durationSeconds,
        startTime: timed ? (end ? start : entry.startTime) : null,
        endTime: timed ? (end || entry.endTime) : null,
        description: entry.description || '',
        rdEligible: entry.rdEligible,
      });
      showToast('success', 'Entry duplicated.');
    } catch (err) {
      showToast('error', String(err?.message || err));
    }
  };

  const [hideWeekends, setHideWeekends] = useState(readHideWeekends);
  const weekDays = useMemo(
    () => (hideWeekends ? visibleDays.filter((d) => !isWeekend(d)) : visibleDays),
    [hideWeekends, visibleDays]
  );
  const hiddenWeekendSeconds = view === 'week' && hideWeekends
    ? visibleEntries.filter((e) => isWeekend(e.date)).reduce((s, e) => s + e.durationSeconds, 0)
    : 0;

  // Entries on their way to another day, shown there right away: { [entryId]: date }.
  const [movingTo, setMovingTo] = useState({});
  const weekEntries = useMemo(
    () => visibleEntries.map((e) => (movingTo[e.id] ? { ...e, date: movingTo[e.id] } : e)),
    [visibleEntries, movingTo]
  );

  // Keeps its times. Where overlaps are off and something's already there, it goes to the
  // nearest open spot that day instead, the same length.
  const moveEntry = async (entry, toDate) => {
    if (toDate === entry.date) return;
    const timed = Boolean(entry.startTime && entry.endTime);
    const dayEntries = entriesForDate(toDate);
    let times = {};
    if (timed && config?.settings?.allowOverlaps === false
      && findOverlap({ entries: dayEntries, startTime: entry.startTime, endTime: entry.endTime, excludeId: entry.id })) {
      const slot = findOpenSlot({ entries: dayEntries, startTime: entry.startTime, endTime: entry.endTime, excludeId: entry.id });
      if (!slot) {
        showToast('warning', `There's no open ${formatHM(entry.durationSeconds)} on ${formatDate(toDate, { weekday: 'short', month: 'short', day: 'numeric' })}.`);
        return;
      }
      times = { startTime: slot.startTime, endTime: slot.endTime };
    }
    setMovingTo((prev) => ({ ...prev, [entry.id]: toDate }));
    try {
      await saveEntry.mutateAsync({ id: entry.id, date: toDate, durationSeconds: entry.durationSeconds, ...times });
      showToast('success', `Moved to ${formatDate(toDate, { weekday: 'short', month: 'short', day: 'numeric' })}${times.startTime ? `, ${formatClock12(times.startTime)}` : ''}.`);
    } catch (err) {
      showToast('error', String(err?.message || err));
    } finally {
      setMovingTo(({ [entry.id]: _done, ...rest }) => rest);
    }
  };

  const removeEntry = async (entry) => {
    try {
      await deleteEntry.mutateAsync(entry.id);
      showToast('success', 'Entry deleted.');
    } catch (err) {
      showToast('error', String(err?.message || err));
    }
  };

  // At the times it's given (a calendar event's). Otherwise it follows on from the day's last
  // entry; on a day with no times it's just hours. A failure is thrown on, so the form stays
  // open with what was typed.
  const quickAddEntry = async ({ taskId, date: day, durationSeconds, description, ...given }) => {
    const start = suggestStartTime(entriesForDate(day));
    const end = start ? addToClock(start, durationSeconds) : null;
    const times = 'startTime' in given ? given : { startTime: end ? start : null, endTime: end };
    try {
      await saveEntry.mutateAsync({
        employeeId,
        taskId,
        date: day,
        durationSeconds,
        ...times,
        description,
      });
      showToast('success', `Logged ${formatHM(durationSeconds)}.`);
    } catch (err) {
      showToast('error', String(err?.message || err));
      throw err;
    }
  };

  // One-click fixes for the checks that have one (see GET /timesheet/validation).
  // Missing breaks go on the task of that type - zero-length markers, or real
  // minutes when the rule counts minutes - each placed where it falls due;
  // rounding moves the end time with the length when there is one. Locked
  // entries are left alone - the server would refuse them anyway.
  const [fixing, setFixing] = useState(null);
  const applyFix = async (issue) => {
    const { fix } = issue;
    if (!fix || fixing) return;
    setFixing(issue.key);
    try {
      if (fix.kind === 'add_breaks') {
        const breakTask = tasks.find((t) => t.systemRole === fix.type && !t.effectiveArchived);
        const typeName = config?.systemRoles?.find((r) => r.key === fix.type)?.label || 'that type';
        if (!breakTask) {
          showToast('warning', `No task has the ${typeName} type. Set one under Admin > Configuration > Timesheet.`);
          return;
        }
        const dayEntries = entriesForDate(issue.date);
        const seconds = (Number(fix.minutes) || 0) * 60;
        for (let i = 0; i < fix.count; i += 1) {
          const at = breakClock(dayEntries, taskById, fix.dueHours?.[i] ?? (i + 1) * 5);
          const end = at && seconds ? addToClock(at, seconds) : at;
          await saveEntry.mutateAsync({
            employeeId,
            taskId: breakTask.id,
            date: issue.date,
            durationSeconds: seconds,
            startTime: at && end ? at : null,
            endTime: at && end ? end : null,
            description: '',
          });
        }
        showToast('success', fix.count === 1 ? `${typeName} added.` : `${fix.count} × ${typeName} added.`);
      } else if (fix.kind === 'set_duration') {
        const entry = entries.find((e) => e.id === fix.entryId);
        if (!entry || entry.locked) return;
        const end = entry.startTime && entry.endTime ? addToClock(entry.startTime, fix.seconds) : null;
        await saveEntry.mutateAsync({
          id: entry.id,
          durationSeconds: fix.seconds,
          ...(entry.startTime && entry.endTime
            ? { startTime: end ? entry.startTime : null, endTime: end }
            : {}),
        });
        showToast('success', `Rounded to ${formatHM(fix.seconds)}.`);
      }
    } catch (err) {
      showToast('error', String(err?.message || err));
    } finally {
      setFixing(null);
    }
  };

  const runAction = async (comment) => {
    const meta = PERIOD_ACTIONS[actionKey];
    setActionError(null);
    try {
      // Submitting defaults to the caller; the review actions always name whose
      // timesheet it is.
      const targetId = isSelf ? (actionKey === 'submit' ? undefined : auth.employee?.id) : employeeParam;
      await periodAction.mutateAsync({ action: actionKey, employeeId: targetId, start: period.start, comment: comment || undefined });
      showToast('success', meta.done);
      setActionKey(null);
    } catch (err) {
      setActionError(String(err?.message || err));
    }
  };

  const employeeName = entriesQuery.data?.employee?.name;
  const employeeOptions = useMemo(
    () => (employeesQuery.data?.employees || [])
      .filter((e) => e.isActive || e.id === employeeParam)
      .map((e) => ({ value: e.id, label: e.name })),
    [employeesQuery.data, employeeParam]
  );

  const loadError = configQuery.error || tasksQuery.error || entriesQuery.error || periodQuery.error;
  const p = periodData?.period;

  // Moving to another day (week, pay period) slides the heading and the
  // entries over toward the side we moved to; switching views just swaps.
  // Worked out during render so the first frame already has it.
  const anchor = view === 'day' ? date : view === 'week' ? week.start : period.start;
  const slideKey = `${view}:${anchor}`;
  const slide = useRef({ key: slideKey, view, anchor, dir: 0 });
  if (slide.current.key !== slideKey) {
    const sameView = slide.current.view === view;
    slide.current = { key: slideKey, view, anchor, dir: sameView ? (anchor > slide.current.anchor ? 1 : -1) : 0 };
  }
  const slideDir = slide.current.dir;

  useTimesheetTutorial({
    view,
    date,
    today,
    period,
    editable,
    entries,
    tasks,
    timerRunning: Boolean(timerQuery.data?.timer),
    canUseCalendar: Boolean(perms.canUseCalendar && isSelf),
    show: ({ view: nextView, date: nextDate }) => updateParams({
      view: nextView === 'day' ? null : nextView,
      date: nextDate === today ? null : nextDate,
      highlight: null,
    }),
    deleteEntry: deleteEntry.mutateAsync,
  }, {
    // Your own timesheet, once it's loaded. Impersonating can only read, so it couldn't be done.
    autoStart: isSelf && !auth.impersonatedBy && installed && Boolean(periodData) && !entriesQuery.isLoading && !tasksQuery.isLoading,
  });

  // Going to a check: the day view of its day, with the check itself scrolled to and pulsing. The
  // day may still have to load, so it looks for a few seconds. A check with no day of its own sits
  // under whichever day is showing.
  const [jumpTo, setJumpTo] = useState(null);
  const goToIssue = (issue) => {
    setActionKey(null);
    setActionError(null);
    const day = issue.date || date;
    updateParams({ view: null, date: day === today ? null : day, highlight: null });
    setJumpTo(issue.key);
  };
  useEffect(() => {
    if (!jumpTo) return undefined;
    const key = JSON.stringify(jumpTo);
    const startedAt = Date.now();
    const timer = setInterval(() => {
      const banner = document.querySelector(`[data-issue-key=${key}]`);
      const flag = banner ? null : document.querySelector(`[data-issue-keys~=${key}]`);
      const found = banner || flag?.closest('.ts-agenda__row');
      if (!found && Date.now() - startedAt < 4000) return;
      clearInterval(timer);
      setJumpTo(null);
      if (!found) return;
      found.scrollIntoView({ block: 'center', behavior: 'smooth' });
      found.classList.remove('ts-issue-jump');
      found.classList.add('ts-issue-jump');
      setTimeout(() => found.classList.remove('ts-issue-jump'), 2000);
    }, 100);
    return () => clearInterval(timer);
  }, [jumpTo]);

  const label = view === 'day'
    ? formatDayHeading(date)
    : view === 'week'
      ? formatRange(week.start, week.end)
      : `Pay period, ${formatPeriod(period)}`;

  // Only worth a banner when the period is in somebody's hands: submitted,
  // approved, or sent back with something to fix.
  const statusBanner = (() => {
    if (!p || status === 'open') return null;
    if (status === 'submitted') {
      return { tone: 'info', icon: 'bi-send', text: `Submitted ${formatTimestamp(p.submittedAt)}${p.submittedBy ? ` by ${p.submittedBy}` : ''}. Waiting for approval, so this period's time is locked.` };
    }
    if (status === 'approved') {
      return { tone: 'good', icon: 'bi-check-circle', text: `Approved ${formatTimestamp(p.reviewedAt)}${p.reviewedBy ? ` by ${p.reviewedBy}` : ''}.${p.reviewComment ? ` “${p.reviewComment}”` : ''}` };
    }
    return { tone: 'warn', icon: 'bi-arrow-counterclockwise', text: `Returned ${formatTimestamp(p.reviewedAt)}${p.reviewedBy ? ` by ${p.reviewedBy}` : ''}: “${p.reviewComment || 'No comment'}”. Fix it up and submit again.` };
  })();

  return (
    <div className="bdc-page ts-page">
      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Timesheet' }]}
        icon="bi-stopwatch"
        title={isSelf ? 'Timesheet' : `${employeeName || 'Employee'}’s Timesheet`}
        actions={(
          <TimesheetNav config={config}>
            {isSelf ? (
              <TutorialButton tutorialId={TIMESHEET_TUTORIAL} icon="question-circle" className="ts-help-btn">
                Help
              </TutorialButton>
            ) : null}
          </TimesheetNav>
        )}
        showBack={!isSelf}
        backTo="/timesheet/approvals"
        backLabel="Approvals"
      />

      <ProviderBanner config={config} />

      <div className="ts-rangebar">
        <div className="ts-rangebar__row">
          <div className="ts-rangebar__where">
            <div className="ts-viewtabs" role="tablist" aria-label="View">
              {VIEWS.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  role="tab"
                  data-tutorial={`view-${v.key}`}
                  className="ts-viewtabs__tab"
                  aria-selected={view === v.key}
                  onClick={() => updateParams({ view: v.key === 'day' ? null : v.key, highlight: null })}
                >
                  {v.label}
                </button>
              ))}
            </div>
            <DateStepper
              date={date}
              label={label}
              unit={view === 'period' ? 'pay period' : view}
              onStep={step}
              onJump={goTo}
              onToday={() => goTo(today)}
              showToday={date !== today}
              slideKey={slideKey}
              slideDir={slideDir}
            />
          </div>
          <div className="ts-rangebar__tools">
            {view !== 'day' && isSelf && timerQuery.data?.timer ? (
              <button type="button" className="bdc-btn bdc-btn--sm" onClick={() => updateParams({ view: null, highlight: null })}>
                <i className="bi bi-stopwatch" aria-hidden="true" />
                Timer running
              </button>
            ) : null}
            {view !== 'day' && periodData ? (
              <>
                <StatusPill status={status} />
                <PeriodActions actions={actions} onAction={setActionKey} compact />
              </>
            ) : null}
            {view === 'week' ? (
              <span className="ts-rangebar__weekends">
                <Checkbox
                  checked={hideWeekends}
                  onChange={(_value, checked) => {
                    setHideWeekends(checked);
                    writeHideWeekends(checked);
                  }}
                >
                  Hide Sat/Sun
                </Checkbox>
                {hiddenWeekendSeconds ? (
                  <span className="ts-rangebar__hidden" title="Logged on the days not showing">{formatHM(hiddenWeekendSeconds)} hidden</span>
                ) : null}
              </span>
            ) : null}
            {canPickEmployee ? (
              <div className="ts-rangebar__person">
                <SelectPicker
                  data={employeeOptions}
                  value={isSelf ? (auth.employee?.id ? String(auth.employee.id) : null) : employeeParam}
                  onChange={(value) => updateParams({ employee: value && value !== String(auth.employee?.id) ? value : null, highlight: null })}
                  placeholder="Whose timesheet"
                  loading={employeesQuery.isLoading}
                  cleanable={false}
                  renderValue={(value, item) => (
                    <span><i className="bi bi-person me-2" aria-hidden="true" />{item?.label || 'Me'}</span>
                  )}
                  block
                  preventOverflow
                />
              </div>
            ) : null}
            <button
              type="button"
              className="ts-toolicon"
              onClick={() => {
                entriesQuery.refetch();
                periodQuery.refetch();
              }}
              aria-label="Refresh"
              title="Refresh"
            >
              <i className={`bi bi-arrow-repeat${entriesQuery.isFetching ? ' spin' : ''}`} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {loadError ? <Message type="error" showIcon>{String(loadError?.message || loadError)}</Message> : null}

      <div className="ts-layout">
        <div className="ts-layout__main">
          {statusBanner ? (
            <Banner tone={statusBanner.tone} icon={statusBanner.icon} title={`Pay period ${formatPeriod(period)}`}>
              {statusBanner.text}
            </Banner>
          ) : null}

          <SlideSwap swapKey={slideKey} direction={slideDir}>
            {entriesQuery.isLoading || tasksQuery.isLoading ? (
              <div className="ts-empty"><i className="bi bi-hourglass-split" aria-hidden="true" />Loading timesheet…</div>
            ) : view === 'day' ? (
              <DayView
                date={date}
                entries={entriesForDate(date)}
                tasks={tasks}
                taskById={taskById}
                config={config}
                editable={editable}
                highlightTaskId={searchParams.get('highlight')}
                dayIssues={issues.allByDate.get(date) || []}
                entryIssues={issues.byEntry}
                onFix={applyFix}
                fixing={fixing}
                onDuplicate={duplicateEntry}
                onQuickAdd={quickAddEntry}
                showHistory={auth.isAdmin()}
              />
            ) : view === 'week' ? (
              <WeekColumns
                days={weekDays}
                today={today}
                entries={weekEntries}
                tasks={tasks}
                taskById={taskById}
                config={config}
                editable={editable}
                onOpen={(entry) => setModal({ entry })}
                onMove={moveEntry}
                onClone={duplicateEntry}
                onDelete={removeEntry}
                onQuickAdd={quickAddEntry}
                onDayClick={(d) => updateParams({ view: null, date: d === today ? null : d, highlight: null })}
                dayFlags={issues.flagsByDate}
              />
            ) : (
              <PeriodGrid
                days={visibleDays}
                today={today}
                rowTaskIds={rowTaskIds}
                taskById={taskById}
                cellMap={cellMap}
                tasks={tasks}
                config={config}
                editable={editable}
                onCellClick={openCell}
                onQuickAdd={quickAddEntry}
                onDayClick={(d) => updateParams({ view: null, date: d === today ? null : d, highlight: null })}
                dayFlags={issues.flagsByDate}
              />
            )}
          </SlideSwap>

          {view === 'day' && isSelf && editable && perms.canUseCalendar ? (
            <CalendarEvents
              date={date}
              week={week}
              entries={entriesForDate(date)}
              tasks={tasks}
              config={config}
              onQuickAdd={quickAddEntry}
            />
          ) : null}

          <ValidationBanners issues={issues.period} />

          {periodData?.events?.length ? (
            <Section title="Approval History" count={periodData.events.length} collapsible>
              <ul className="ts-checklist">
                {periodData.events.map((ev, idx) => (
                  <li key={`${ev.at}-${idx}`}>
                    <i className="bi bi-dot" aria-hidden="true" />
                    <span>
                      <strong className="text-capitalize">{ev.action}</strong>
                      {ev.by ? ` by ${ev.by}` : ''} · {formatTimestamp(ev.at)}
                      {ev.comment ? <> — “{ev.comment}”</> : null}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
        </div>

        {view === 'day' ? (
          <aside className="ts-layout__side" aria-label="Log time and pay period">
            {installed && isSelf && (editable || timerQuery.data?.timer) ? (
              <LogTimeBar tasks={tasks} config={config} date={date} entriesForDate={entriesForDate} />
            ) : null}
            {periodData ? (
              <PeriodCard
                period={period}
                status={status}
                seconds={periodSeconds}
                billableSeconds={periodBillableSeconds}
                missingDays={status === 'approved' ? [] : missingDays}
                issueCount={status === 'approved' ? 0 : issueCount}
                failCount={issues.failCount}
                onShowIssue={issues.all.length ? () => goToIssue(issues.all[0]) : null}
                actions={actions}
                onAction={setActionKey}
              />
            ) : null}
          </aside>
        ) : null}
      </div>

      <EntryModal
        open={Boolean(modal)}
        onClose={() => setModal(null)}
        entry={modal?.entry || null}
        employeeId={employeeId}
        tasks={tasks}
        config={config}
        readOnly={!editable}
      />

      <PeriodActionModal
        open={Boolean(actionKey)}
        onClose={() => {
          setActionKey(null);
          setActionError(null);
        }}
        onConfirm={runAction}
        loading={periodAction.isPending}
        error={actionError}
        {...PERIOD_ACTIONS[actionKey]}
      >
        <p className="mb-0">
          {actionKey === 'submit'
            ? `You are submitting ${formatHM(periodSeconds)} for ${formatPeriod(period)}. Once submitted, the time is locked until it is reviewed.`
            : actionKey === 'approve'
              ? `Approve ${formatHM(periodSeconds)} for ${formatPeriod(period)}? Approved time is locked.`
              : actionKey === 'return'
                ? 'The timesheet goes back to its owner to fix and resubmit.'
                : 'Reopening unlocks the time so it can be edited and submitted again.'}
        </p>
        {actionKey === 'submit' && issueCount ? (
          <div className="ts-submitchecks">
            <div className="ts-submitchecks__title">
              {issueCount} check{issueCount === 1 ? '' : 's'} flagged on this period
            </div>
            <ul className="ts-submitchecks__list">
              {issues.all.map((issue) => (
                <li key={issue.key} className="ts-submitchecks__item">
                  <i className={`bi bi-exclamation-triangle-fill ts-flag ts-flag--${issue.severity}`} aria-hidden="true" />
                  <div className="ts-submitchecks__body">
                    <strong>{issue.name}</strong>
                    <span>
                      {issue.date ? `${formatDate(issue.date, { weekday: 'short', month: 'short', day: 'numeric' })}: ` : ''}
                      {issue.detail}
                    </span>
                  </div>
                  <button type="button" className="bdc-btn bdc-btn--sm" onClick={() => goToIssue(issue)}>
                    {issue.date ? 'Go to day' : 'Show'}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {actionKey === 'submit' && missingDays.length ? (
          <Message type="warning" showIcon>
            {missingDays.length} weekday{missingDays.length === 1 ? ' has' : 's have'} nothing logged.
          </Message>
        ) : null}
      </PeriodActionModal>
    </div>
  );
}
